import { reaction } from 'mobx';
import i18n from '@i18next/i18n';
import {
    NOTE_COLLAPSE_HEIGHT,
    NOTE_MAX_HEIGHT,
    NOTE_MIN_HEIGHT,
    NOTE_START_COLLAPSE_HEIGHT,
    NOTE_DEFAULT_HEIGHT,
} from '@editor/src/js/constants';
import _throttle from 'lodash/throttle';
import { tdw } from '@tencent/tencent-doc-report';
import { TDWActionType, TDWModuleType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { countWords } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/utils/text-util';
import BaseNoteAreaService from '@src/slide/application/service/note-area-component/core/base/base-note-area-service';
import { IPcNoteAreaServiceDelegates, IPcNoteAreaServiceLogic } from '@src/slide/application/service/note-area-component/core/pc/pc-note-area-service.interface';
import BaseViewModel from '@src/slide/infrastructure/module/independent/view-service-define/base/base-view-model';
import PcNoteAreaModel from '@src/slide/application/service/note-area-component/core/pc/pc-note-area-model';
import { IPcNoteAreaProps } from '@src/slide/application/service/note-area-component/core/pc/type';
import { NoteLayerService } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-pc/note';
import { getHeightFixedByNoteHeight } from '@src/slide/application/service/note-area-component/core/base/note-height/get-fixed-note-height';
import debounce from 'lodash/debounce';

const MIN_CANVAS_HEIGHT = 167;
const DEFAULT_OFFSET_VAL = 0;

export class PcNoteAreaService extends BaseNoteAreaService<IPcNoteAreaProps>
    implements IPcNoteAreaServiceLogic {
    declare protected services?: IPcNoteAreaServiceDelegates;

    private resistNoteAreaHeight: number;
    private noteAreaHeight: number;
    private rawNoteAreaHeight: any;
    private noteLayer: NoteLayerService;

    public constructor() {
        super();
        this.noteAreaHeight = NOTE_DEFAULT_HEIGHT;
        this.resistNoteAreaHeight = NOTE_DEFAULT_HEIGHT;
    }

    public componentDidMount(): void {
        this.renderCanvasNote();
        super.componentDidMount();
        this.viewDelegate?.disposeOnUnmount(reaction(() => this.services?.noteAreaManager?.show, (noteShow) => {
            // 因为有setState，所以有不同步的情况，采用同步的this.noteAreaHeight判断
            if (noteShow) {
                if (this.noteAreaShowFlag()) return;
                if (!this.judgeNoteAreaCanShow()) {
                    this.services?.tipsManager?.showMsg(i18n.t('请拉高页面后，再查看备注。'));
                    this.services?.noteAreaManager?.setHide();
                }
                const height = this.fixNoteHeight(this.resistNoteAreaHeight);
                this.setNoteAreaHeight(height);
            } else {
                if (!this.noteAreaShowFlag()) return;
                this.setNoteAreaHeight(NOTE_COLLAPSE_HEIGHT);
                this.hideNote();
            }
            this.quickSettingUpdateRect();
        }));
        this.viewDelegate?.disposeOnUnmount(reaction(() => this.services?.selectionService?.currentSlide, () => {
            // 监听slide变更
            this.updateModel<PcNoteAreaModel>({});
        }));
        this.reportNoteSize();
    }

    public componentDidUpdate(): void {
        this.renderNoteDebounce();
    }

    /**
     * 计算note-area大小
     */
    public fixNoteHeightByResize = () => {
        const offsetHeight = this.getWrapperHeight();

        let height = getHeightFixedByNoteHeight(offsetHeight, this.model.noteAreaHeight);

        if (height >= MIN_CANVAS_HEIGHT) {
            return; // 无需处理
        }
        // 此时根据交互，canvas高度无法放下，因此需要缩小note-area高度
        height = offsetHeight - MIN_CANVAS_HEIGHT;
        height = this.fixNoteHeight(height);
        if (this.noteAreaHeight !== height) {
            this.setNoteAreaHeight(height);
        }
        // 关闭了的话手动触发一下...
        if (height <= NOTE_COLLAPSE_HEIGHT) {
            this.hideNote();
        } else {
            this.resistNoteAreaHeight = this.noteAreaHeight;
            this.services?.noteAreaManager?.setShow();
        }
    };

    /**
     * 判断note-area内部是否需要显示
     * 当高度为collapse-height的时候不显示
     */
    public noteAreaShowFlag = () => this.noteAreaHeight !== NOTE_COLLAPSE_HEIGHT;

    /**
     * 开始拖动，判断是否能拉大，不能的话弹出提示
     */
    public handleSplitLineBeforeDrag = () => {
        this.rawNoteAreaHeight = this.model.noteAreaHeight;
        if (!this.judgeNoteAreaCanShow()) {
            this.services?.tipsManager?.showMsg(i18n.t('请拉高页面后，再查看备注。'));
        }
    };

    /**
     * 拖动过程中，若能拖动，即时修改高度
     */
    // eslint-disable-next-line @typescript-eslint/member-ordering
    public handleSplitLineOnDrag = _throttle((e, { offsetY } = {}) => {
        if (!this.judgeNoteAreaCanShow()) return;

        let noteAreaHeight = this.rawNoteAreaHeight - (offsetY || 0);
        noteAreaHeight = this.fixNoteHeight(noteAreaHeight);

        if (noteAreaHeight !== this.model.noteAreaHeight) {
            this.setNoteAreaHeight(noteAreaHeight);
        }
    });

    /**
     * 拖动完成后，判断是否显示
     */
    public handleSplitLineAfterDrag = () => {
        // 当noteAreaHeight为NOTE_COLLAPSE_HEIGHT的时候，需要将显示设置为false...
        if (!this.noteAreaShowFlag()) {
            this.hideNote();
            this.resistNoteAreaHeight = NOTE_MIN_HEIGHT;
        } else {
            this.services?.noteAreaManager?.setShow();
            this.resistNoteAreaHeight = this.noteAreaHeight;
        }
    };

    protected createViewModel(): BaseViewModel<{}> {
        return new PcNoteAreaModel();
    }

    // canvas模式下 渲染note
    private renderCanvasNote(): void {
        if (this.noteCanvasRef) {
            const noteAreaWidth = this.noteContainerRef?.getBoundingClientRect?.()?.width || 0;
            if (!this.noteLayer) {
                this.noteLayer = new NoteLayerService({
                    canvasElement: this.noteCanvasRef,
                    width: noteAreaWidth,
                    height: this.model.noteAreaHeight,
                    topBlankDiv: this.noteTopDivRef,
                    bottomBlankDiv: this.noteBottomDivRef,
                    getDisplayStatus: () => !this.services?.playManager?.isPlaying,
                    uncommonFontLogic: this.services?.fontManager?.uncommonFontLogic,
                });
            } else {
                this.noteLayer.updateWithData(noteAreaWidth, this.model.noteAreaHeight);
            }
        }
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private renderNoteDebounce = debounce(() => {
        this.renderCanvasNote();
    }, 200);

    /**
     * @param {number} rawHeight 设定的高度
     * @returns {number} height 计算得出的可显示高度
     */
    private fixNoteHeight = (rawHeight) => {
        let height = rawHeight;

        const offsetHeight = this.getWrapperHeight();

        // 高度是计算高度，最大高度，最大可得高度中最小的一个
        height = Math.min(height, NOTE_MAX_HEIGHT, offsetHeight - MIN_CANVAS_HEIGHT);

        // 极小值下直接跳变
        if (height < NOTE_START_COLLAPSE_HEIGHT) {
            height = NOTE_COLLAPSE_HEIGHT;
        } else if (height < NOTE_MIN_HEIGHT) {
            height = NOTE_MIN_HEIGHT;
        }

        return height;
    };

    /**
     * 判断页面能否显示note-area
     */
    private judgeNoteAreaCanShow = () => {
        const offsetHeight = this.getWrapperHeight();

        return offsetHeight >= (MIN_CANVAS_HEIGHT + NOTE_MIN_HEIGHT);
    };

    /**
     * 设置对应的noteareaheight高度
     * @param {number} noteAreaHeight
     */
    private setNoteAreaHeight = (noteAreaHeight) => {
        this.noteAreaHeight = noteAreaHeight;
        this.updateModel<PcNoteAreaModel>({ noteAreaHeight }, () => {
            // note-area高度变化触发的resize，不需要修正noteheight变化
            this.services?.canvasService?.resize(null, null, { noteHeightChange: false });
        });
    };

    // 更新QuickSetting
    private quickSettingUpdateRect = () => {
        import(/* webpackChunkName: "service" */ '@com/quick-setting/quick-setting-manager')
            .then((module) => {
                const QuickSettingManager = module.default;
                QuickSettingManager.getInstance().updateAnchorRect();
            })
            .catch((error) => {
                window.log.error(`[loadChunk-error]${error}`);
            });
    };

    private reportNoteSize = () => {
        let noteMaxSize = 0;
        const slideSize = this.services?.selectionService?.slides.length;
        this.services?.selectionService?.slides.forEach((slide) => {
            const num = countWords(slide.noteData?.noteTextBody?.paragraphList);
            noteMaxSize = Math.max(noteMaxSize, num);
        });
        tdw({
            module: TDWModuleType.SLIDE_PC_NOTE,
            action: TDWActionType.SLIDE_NOTE_SIZE,
            ver6: noteMaxSize,
            ver7: slideSize,
        });
    };


    private getWrapperHeight(): number {
        return this.services?.canvasService?.getCanvasSizeHandler().getWrapperHeightAndWidth().offsetHeight
            ?? DEFAULT_OFFSET_VAL;
    }
}
